import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import ForecastUpdates from "~/components/forecast/tabContent/ForecastUpdates"
import { useFilters } from "~/hooks/useFilters"
import { useHistoricForecastComparison } from "~/hooks/useForecastComparison"
import { useNetFactors } from "~/hooks/useNetFactors"
import { useVariantContext } from "~/context/VariantProvider"
import { api } from "~/utils/api"
import { filterVariants, type FilterableVariant } from "~/utils/filter"
import {
  computeForecastChanges,
  forecastRangeOptions,
  startDate,
} from "~/utils/forecastChangeMetrics"
import { NOOS_FORECAST_UPDATES_TABLE_ID } from "~/constants"
import { useTableSearch } from "~/hooks/useTableSearch"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useStableValue } from "~/hooks/useStableValue"

export const Route = createFileRoute(
  "/_auth/demand-planning/noos/$collectionId/_layout/forecast-updates",
)({
  component: RouteComponent,
})

// Reused while its inputs are unchanged, so the grid re-processes only edited rows.
const enrichedRows = new WeakMap<
  object,
  { ranges: unknown; toNet: unknown; attachNet: boolean; row: FilterableVariant }
>()

const sameRows = (a: readonly object[], b: readonly object[]) =>
  a.length === b.length && a.every((row, i) => row === b[i])

function RouteComponent() {
  const { effectiveSearchText } = useTableSearch(NOOS_FORECAST_UPDATES_TABLE_ID)
  const { filters } = useFilters()
  const { variants: rawVariants } = useVariantContext()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)

  const variantIds = useMemo(() => rawVariants.map((v) => v.variantId), [rawVariants])
  // No channel: forecastQuantity comes from getAllForecasts which aggregates across channels, so
  // the net factor must be the all-channel blend to match it.
  const netFactors = useNetFactors({ variantIds, warehouseId: selectedWarehouseId })
  const { netMode, isPending: netPending, toNet: liveToNet } = netFactors
  // While net factors load toNet yields only nulls: the rows keep their last values, which the
  // change cells hide behind a placeholder, and are enriched once when the factors arrive.
  const toNet = useStableValue(liveToNet, () => netPending)

  const { selectedForecast } = useHistoricForecastComparison()

  // Keyed on the unfiltered variants so the query key doesn't churn on every filter edit.
  const { data: forecastRanges } = api.forecasts.getHistoricForecastRanges.useQuery(
    { variantIds, date: selectedForecast?.value ?? startDate, dateRanges: forecastRangeOptions },
    { enabled: !!selectedForecast && variantIds.length > 0 },
  )

  // Rows carry every value a filter can compare so the filters and the cells read the same
  // numbers: the demand filter follows the gross/net toggle via the same toNet the cells use,
  // and the month-change filters read the same live-forecast-vs-snapshot deltas as the change
  // cells. netForecastQuantity is an extra field rather than replacing forecastQuantity, which
  // the column's own value getter would then scale a second time.
  const attachNet = netMode && !netPending
  const rowsToFilter = useMemo<FilterableVariant[]>(() => {
    if (!forecastRanges && !attachNet) return rawVariants
    return rawVariants.map((v) => {
      const hit = enrichedRows.get(v)
      if (
        hit &&
        hit.ranges === forecastRanges &&
        hit.toNet === toNet &&
        hit.attachNet === attachNet
      ) {
        return hit.row
      }
      const row = {
        ...v,
        netForecastQuantity: attachNet
          ? (toNet(v.variantId, v.forecastQuantity) ?? v.forecastQuantity)
          : undefined,
        forecastChanges: forecastRanges
          ? computeForecastChanges(forecastRanges.get(v.variantId), v.variantForecast, (value) =>
              toNet(v.variantId, value),
            )
          : undefined,
      }
      enrichedRows.set(v, { ranges: forecastRanges, toNet, attachNet, row })
      return row
    })
  }, [rawVariants, attachNet, toNet, forecastRanges])

  const rawById = useMemo(() => new Map(rawVariants.map((v) => [v.variantId, v])), [rawVariants])
  const forecastChanges = useMemo(
    () => new Map(rowsToFilter.map((r) => [r.variantId, r.forecastChanges])),
    [rowsToFilter],
  )

  // The grid gets the original row objects: AG Grid skips rows whose reference is unchanged, so a
  // gross/net toggle doesn't re-process every row. Enriched values reach the cells via forecastChanges.
  // Kept when a toggle filters to the same rows, so the grid isn't handed new row data.
  const variants = useStableValue(
    useMemo(() => {
      return filterVariants(filters, rowsToFilter, effectiveSearchText).map(
        (r) => rawById.get(r.variantId)!,
      )
    }, [rowsToFilter, filters, effectiveSearchText, rawById]),
    sameRows,
  )

  return (
    <ForecastUpdates
      variants={variants}
      allVariantIds={variantIds}
      tableId={NOOS_FORECAST_UPDATES_TABLE_ID}
      netFactors={netFactors}
      forecastChanges={forecastChanges}
    />
  )
}
